/**
 * StaffPayRatesTab (11F.7) — ref `staff3.png`.
 *
 * A rate is keyed by (subject × duration). The grid below therefore has one
 * row per subject the staff member teaches, with an amount input per
 * duration offered.
 *
 * Amounts are edited in RUPEES and stored in PAISE — the conversion happens
 * once on save. Every money field in the system is minor-units server-side,
 * and doing the conversion at the boundary is what keeps `₹36.00` from ever
 * being written as `3600.00` rupees.
 *
 * Subjects come from a render-prop rather than a direct import: People must
 * not import Catalog (eslint feature boundary).
 */

import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import toast from 'react-hot-toast';

import { Button, Card, Input } from '@/components/common';
import { ApiError } from '@core/api/client';
import {
  useStaffPayRates,
  useUpdateStaffPayRates,
  type StaffPayRate,
} from '@features/people/api/staff';

/** Durations a rate can be set for. Mirrors the session lengths the
 *  scheduling side offers; a subject taught only at 60 min simply leaves
 *  the other column blank. */
const DURATIONS = [60, 90] as const;

const Grid = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};

  th {
    text-align: left;
    padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.sm}
      ${({ theme }) => theme.spacing.sm} 0;
    font-size: ${({ theme }) => theme.typography.fontSizes.xs};
    text-transform: uppercase;
    letter-spacing: ${({ theme }) => theme.typography.letterSpacing.wide};
    color: ${({ theme }) => theme.colors.textTertiary};
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  }
  td {
    padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.sm}
      ${({ theme }) => theme.spacing.sm} 0;
    border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
    vertical-align: middle;
  }
`;

const Scroll = styled.div`
  overflow-x: auto;
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-top: ${({ theme }) => theme.spacing.lg};
`;

export interface PayRateSubject {
  id: string;
  name: string;
}

interface Props {
  staffId: string;
  /** Subjects this staff member can be paid for — injected from Catalog. */
  subjects?: PayRateSubject[];
  /** False for a viewer who may read rates but not change them. */
  canEdit?: boolean;
}

type Draft = Record<string, string>; // `${subjectId}:${duration}` → rupees

function keyOf(subjectId: string, duration: number): string {
  return `${subjectId}:${duration}`;
}

export const StaffPayRatesTab: React.FC<Props> = ({
  staffId,
  subjects,
  canEdit = true,
}) => {
  const { data, isLoading, isError } = useStaffPayRates(staffId);
  const save = useUpdateStaffPayRates(staffId);
  const [draft, setDraft] = useState<Draft>({});

  // Seed the editable draft from the server response once it lands.
  useEffect(() => {
    if (!data) return;
    const next: Draft = {};
    for (const r of data.rates) {
      next[keyOf(r.subject_id, r.duration_minutes)] = (r.amount / 100).toFixed(2);
    }
    setDraft(next);
  }, [data]);

  /**
   * Rows to render: the injected subject list when available, otherwise the
   * subjects already carrying a rate. The fallback matters — without a
   * subject list we must still show (and not silently drop) existing rates.
   */
  const rows: PayRateSubject[] = useMemo(() => {
    if (subjects?.length) return subjects;
    const seen = new Map<string, string>();
    for (const r of data?.rates ?? []) {
      seen.set(r.subject_id, r.subject_name || r.subject_id);
    }
    return [...seen].map(([id, name]) => ({ id, name }));
  }, [subjects, data]);

  const handleSave = async () => {
    const rates: StaffPayRate[] = [];
    for (const subject of rows) {
      for (const duration of DURATIONS) {
        const raw = draft[keyOf(subject.id, duration)];
        if (raw === undefined || raw.trim() === '') continue; // blank = no rate
        const rupees = Number.parseFloat(raw);
        if (!Number.isFinite(rupees) || rupees < 0) {
          toast.error(`Enter a valid amount for ${subject.name} (${duration} min).`);
          return;
        }
        rates.push({
          subject_id: subject.id,
          duration_minutes: duration,
          amount: Math.round(rupees * 100), // rupees → paise, once
        });
      }
    }
    try {
      await save.mutateAsync(rates);
      toast.success('Pay rates saved.');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not save pay rates.');
    }
  };

  if (isError) {
    return (
      <p style={{ color: '#dc2626' }} data-testid="candeza-staff-payrates-error">
        Could not load pay rates. You may not have permission to view salary data.
      </p>
    );
  }

  return (
    <div data-testid="candeza-staff-payrates-tab">
      <Card padding="lg">
        {isLoading ? (
          <p style={{ color: '#64748b' }}>Loading pay rates…</p>
        ) : rows.length === 0 ? (
          <p style={{ color: '#94a3b8' }} data-testid="candeza-staff-payrates-empty">
            No subjects assigned to this staff member yet — assign subjects on the
            staff record to set pay rates.
          </p>
        ) : (
          <Scroll>
            <Grid data-testid="candeza-staff-payrates-grid">
              <thead>
                <tr>
                  <th>Subject</th>
                  {DURATIONS.map((d) => (
                    <th key={d}>{d} min (₹)</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((subject) => (
                  <tr key={subject.id} data-testid={`candeza-staff-payrate-row-${subject.id}`}>
                    <td style={{ fontWeight: 500 }}>{subject.name}</td>
                    {DURATIONS.map((duration) => (
                      <td key={duration}>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder="—"
                          disabled={!canEdit}
                          value={draft[keyOf(subject.id, duration)] ?? ''}
                          onChange={(e) =>
                            setDraft((prev) => ({
                              ...prev,
                              [keyOf(subject.id, duration)]: e.target.value,
                            }))
                          }
                          data-testid={`candeza-staff-payrate-${subject.id}-${duration}`}
                          style={{ maxWidth: 130 }}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </Grid>
          </Scroll>
        )}

        {canEdit && rows.length > 0 && (
          <Actions>
            <Button
              onClick={handleSave}
              disabled={save.isPending}
              data-testid="candeza-staff-payrates-save"
            >
              {save.isPending ? 'Saving…' : 'Save pay rates'}
            </Button>
          </Actions>
        )}
      </Card>
    </div>
  );
};

export default StaffPayRatesTab;
